import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Plus } from 'lucide-react';
import Button from '../ui/Button';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import type { InductionList } from '../../types/induction';

interface Props {
  canAssess: boolean;
}

/**
 * Every equipment induction, and the machine types nobody has set one up for.
 * Setting one up copies the template for that type; it is edited afterwards.
 */
export default function EquipmentInductionsPanel({ canAssess }: Props) {
  const [data, setData] = useState<InductionList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await inductionService.list());
    } catch (e) {
      setError(errorMessage(e, 'Could not load inductions'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setUp = async (keys: string[]) => {
    setBusy(keys.length === 1 ? keys[0] : '*');
    setError(null);
    try {
      for (const type_key of keys) await inductionService.create({ type_key });
      await load();
    } catch (e) {
      setError(errorMessage(e, 'Could not set up the induction'));
    } finally {
      setBusy(null);
    }
  };

  if (loading && !data) return <p className="text-sm text-neutral-500">Loading inductions…</p>;

  const inductions = data?.inductions ?? [];
  const uncovered = data?.uncovered_types ?? [];

  return (
    <div className="space-y-6">
      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 break-words">{error}</div>}

      {uncovered.length > 0 && (
        <section className="rounded-lg border border-warning-200 bg-warning-50 p-3 sm:p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1 basis-56">
              <h3 className="font-semibold text-warning-900">Machines with no induction</h3>
              <p className="text-sm text-warning-800 break-words">
                These types of machine are on your equipment register but have no induction yet. Setting one up starts
                from a template you can edit.
              </p>
            </div>
            {canAssess && uncovered.length > 1 && (
              <Button size="sm" onClick={() => setUp(uncovered.map(u => u.type_key))} isLoading={busy === '*'} disabled={!!busy}>
                Set up all {uncovered.length}
              </Button>
            )}
          </div>
          <ul className="mt-3 divide-y divide-warning-200">
            {uncovered.map(u => (
              <li key={u.type_key} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="min-w-0 break-words text-sm text-neutral-800">
                  <span className="font-medium">{u.label}</span>
                  <span className="text-neutral-500"> · {u.machine_count} machine{u.machine_count === 1 ? '' : 's'}</span>
                </span>
                {canAssess && (
                  <Button size="sm" variant="secondary" onClick={() => setUp([u.type_key])} isLoading={busy === u.type_key} disabled={!!busy}>
                    <Plus className="h-4 w-4" /> Set up
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {inductions.length === 0 ? (
        <p className="text-sm text-neutral-500">No equipment inductions yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200">
          {inductions.map(i => (
            <li key={i.id}>
              <Link to={`/inductions/equipment/${i.id}`} className="flex items-center gap-3 p-3 hover:bg-neutral-50">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="font-medium text-neutral-900 break-words min-w-0">{i.title}</span>
                    {i.equipment_id && <Badge className="bg-primary-50 text-primary-700">This machine only</Badge>}
                    {!i.is_required && <Badge className="bg-neutral-100 text-neutral-600">Optional</Badge>}
                    {!i.active && <Badge className="bg-neutral-200 text-neutral-700">Inactive</Badge>}
                  </div>
                  <div className="mt-0.5 text-xs text-neutral-500 break-words">
                    {i.equipment_name ? `${i.equipment_name} · ` : ''}
                    {i.machine_count ?? 0} machine{i.machine_count === 1 ? '' : 's'} · {i.current_count ?? 0} signed off
                    {(i.expiring_count ?? 0) > 0 && <span className="text-warning-800"> · {i.expiring_count} expiring</span>}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-neutral-400" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Badge({ className, children }: { className: string; children: React.ReactNode }) {
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>{children}</span>;
}
